/**
 * Email Campaign Analytics Component (Issue #376)
 *
 * Displays comprehensive performance metrics and analytics for email campaigns.
 * Shows open rates, click rates, conversion rates, timeline data, and more.
 */

import React, { useState, useEffect } from 'react';
import { fairManagerApi } from '../services/api';
import { EmailCampaignAnalytics as AnalyticsData } from '../types';
import { extractApiError } from '../utils/errorUtils'; // Issue #964: Standardized error handling
import {
  TrendingUp,
  Mail,
  MousePointer,
  ShoppingCart,
  ExternalLink,
  Calendar,
  AlertCircle,
  RefreshCw,
} from 'lucide-react';

interface EmailCampaignAnalyticsProps {
  campaignId: number;
}

/**
 * Analytics dashboard for email campaign performance
 */
export const EmailCampaignAnalytics: React.FC<EmailCampaignAnalyticsProps> = ({
  campaignId,
}) => {
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch analytics data
   */
  useEffect(() => {
    const fetchAnalytics = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const data = await fairManagerApi.getEmailCampaignAnalytics(campaignId);
        setAnalytics(data);
      } catch (err: unknown) {
        const apiError = extractApiError(err);
        setError(apiError.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAnalytics();
  }, [campaignId]);

  /**
   * Refresh analytics
   */
  const handleRefresh = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await fairManagerApi.getEmailCampaignAnalytics(campaignId);
      setAnalytics(data);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      setError(apiError.message);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Format date for display
   */
  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  /**
   * Format percentage
   */
  const formatPercentage = (value: number): string => {
    return `${value.toFixed(1)}%`;
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin h-12 w-12 border-4 border-primary border-t-transparent rounded-full" />
          <p className="text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="p-6 bg-red-50 border border-red-200 rounded-md">
        <div className="flex items-center gap-2 text-red-800 mb-2">
          <AlertCircle className="h-5 w-5" />
          <span className="font-semibold">Error Loading Analytics</span>
        </div>
        <p className="text-red-700">{error}</p>
        <button
          onClick={handleRefresh}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
        >
          Try Again
        </button>
      </div>
    );
  }

  // No data
  if (!analytics) {
    return (
      <div className="p-6 bg-gray-50 border border-gray-200 rounded-md text-center">
        <p className="text-gray-600">No analytics data available</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Campaign Analytics</h2>
          <p className="text-gray-600 mt-1">{analytics.subject}</p>
          <p className="text-sm text-gray-500">Sent: {formatDate(analytics.sent_at)}</p>
        </div>
        <button
          onClick={handleRefresh}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
          title="Refresh analytics"
        >
          <RefreshCw className="h-4 w-4" />
          <span>Refresh</span>
        </button>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Sent */}
        <div className="bg-gradient-to-br from-teal-50 to-moonbone p-6 rounded-lg border border-teal-200">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-primary text-white rounded-lg">
              <Mail className="h-6 w-6" />
            </div>
            <h3 className="text-sm font-medium text-ink">Total Sent</h3>
          </div>
          <p className="text-3xl font-bold text-ink">{analytics.total_sent}</p>
          <p className="text-xs text-primary-dark mt-1">emails delivered</p>
        </div>

        {/* Open Rate */}
        <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-lg border border-green-200">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-green-600 text-white rounded-lg">
              <TrendingUp className="h-6 w-6" />
            </div>
            <h3 className="text-sm font-medium text-green-800">Open Rate</h3>
          </div>
          <p className="text-3xl font-bold text-green-900">{formatPercentage(analytics.open_rate)}</p>
          <p className="text-xs text-green-700 mt-1">
            {analytics.total_opened} of {analytics.total_sent} opened
          </p>
        </div>

        {/* Click Rate */}
        <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-lg border border-purple-200">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-purple-600 text-white rounded-lg">
              <MousePointer className="h-6 w-6" />
            </div>
            <h3 className="text-sm font-medium text-purple-800">Click Rate</h3>
          </div>
          <p className="text-3xl font-bold text-purple-900">{formatPercentage(analytics.click_rate)}</p>
          <p className="text-xs text-purple-700 mt-1">
            {analytics.total_clicked} clicks recorded
          </p>
        </div>

        {/* Conversion Rate */}
        <div className="bg-gradient-to-br from-teal-50 to-teal-100 p-6 rounded-lg border border-teal-200">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-primary text-white rounded-lg">
              <ShoppingCart className="h-6 w-6" />
            </div>
            <h3 className="text-sm font-medium text-primary-dark">Conversion Rate</h3>
          </div>
          <p className="text-3xl font-bold text-teal-900">{formatPercentage(analytics.conversion_rate)}</p>
          <p className="text-xs text-primary-dark mt-1">
            {analytics.total_converted} reservations booked
          </p>
        </div>
      </div>

      {/* Timeline Data */}
      {analytics.timeline.length > 0 && (
        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <div className="flex items-center gap-2 mb-4">
            <Calendar className="h-5 w-5 text-gray-600" />
            <h3 className="text-lg font-semibold text-gray-800">Performance Timeline</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 px-4 text-sm font-medium text-gray-700">Date</th>
                  <th className="text-right py-2 px-4 text-sm font-medium text-gray-700">Opens</th>
                  <th className="text-right py-2 px-4 text-sm font-medium text-gray-700">Clicks</th>
                </tr>
              </thead>
              <tbody>
                {analytics.timeline.map((day, index) => (
                  <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-2 px-4 text-sm text-gray-600">{formatDate(day.date)}</td>
                    <td className="py-2 px-4 text-sm text-gray-800 text-right font-medium">
                      {day.opens}
                    </td>
                    <td className="py-2 px-4 text-sm text-gray-800 text-right font-medium">
                      {day.clicks}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Two Column Layout for Links and Conversions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Clicked Links */}
        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <div className="flex items-center gap-2 mb-4">
            <ExternalLink className="h-5 w-5 text-gray-600" />
            <h3 className="text-lg font-semibold text-gray-800">Top Clicked Links</h3>
          </div>
          {analytics.top_clicked_links.length > 0 ? (
            <div className="space-y-3">
              {analytics.top_clicked_links.map((link, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-800 truncate" title={link.link}>
                      {link.link}
                    </p>
                  </div>
                  <span className="ml-4 px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-medium">
                    {link.clicks} clicks
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-sm">No links clicked yet</p>
          )}
        </div>

        {/* Conversions */}
        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <div className="flex items-center gap-2 mb-4">
            <ShoppingCart className="h-5 w-5 text-gray-600" />
            <h3 className="text-lg font-semibold text-gray-800">Conversions</h3>
          </div>
          {analytics.conversions.length > 0 ? (
            <div className="space-y-3">
              {analytics.conversions.map((conversion, index) => (
                <div key={index} className="p-3 bg-teal-50 rounded-md border border-teal-100">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-sm font-medium text-gray-800">{conversion.vendor_name}</p>
                    <span className="text-xs text-gray-500">#{conversion.reservation_id}</span>
                  </div>
                  <p className="text-xs text-gray-600">
                    Booked: {formatDate(conversion.booking_date)}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-sm">No conversions yet</p>
          )}
        </div>
      </div>

      {/* Discount Code Usage */}
      {analytics.discount_code_uses > 0 && (
        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800 mb-2">Discount Code Performance</h3>
          <p className="text-gray-600">
            Discount code was used{' '}
            <span className="font-bold text-secondary">{analytics.discount_code_uses}</span> times
          </p>
        </div>
      )}
    </div>
  );
};

export default EmailCampaignAnalytics;
